import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
 

import { Button, EmptyState } from 'src/DesignSystem';
import {
  surface,
  canvas,
  textPrimary,
  textSecondary,
  textTertiary,
  borderSubtle,
  successText,
  successLight,
  dangerText,
  warningText,
  warningLight,
} from 'src/DesignSystem/tokens/colors';
import { spSm, spMd, spLg } from 'src/DesignSystem/tokens/spacing';
import { radiusMd, radiusLg } from 'src/DesignSystem/tokens/radii';

import { useModal } from 'src/hooks/Modal';
import api from 'src/services/api';
import { addDays } from 'date-fns';
import { FaCircle, FaClock, FaSearch } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';

/* =================================================================== */
/* Types                                                                 */
/* =================================================================== */

export interface ReportRow {
  _id?: string;
  movement_id?: string;
  operation_type?: string;
  operation?: string;
  operation_fee?: number;
  total?: number;
  total_tax?: number;
  fees?: number;
  antecipation_gap?: number;
  administration_tax?: number;
  net_total?: number;
  saldo?: number;    // em centavos
  sacado?: number;   // em centavos (negativo quando há saque)
  date?: string;
  type?: string;
  special_text?: string;
  [key: string]: unknown;
}

export interface WithdrawalBlock {
  rows: ReportRow[];
  total_tax: number;
  fees: number;
  withdraw_tax: number;
  antecipation_gap: number;
  brute: number;
  brute_without_fees: number;
  liquid: number;
  administration_tax: number;
  saldo: number;
  sacado: number;
  sobra: number;
  date_from?: string;
  date_to?: string;
}

export interface AnalysisIssue {
  code: string;
  title: string;
  detail?: string;
}

export interface AnalysisItemRef {
  code: string;
  event: string;
  url: string;        // events: "url"; invoices: "url-invoice" — é o project_id
  payment_id?: string;
  last: string;
  id?: string;        // id do saque (Pagar.me). Ausente = não teve saque.
  saque?: number;     // valor do saque em REAIS (backend faz /100)
}

/* Candidato vindo de finance-report (orphan search) */
export interface OrphanCandidate {
  _id: string;
  project_id: string;
  amount: number;           // convenção do sistema: em centavos
  createdAt: string;
  data?: { id?: string };   // data.id === saque.id (Pagar.me)
}

/* =================================================================== */
/* Utils                                                                 */
/* =================================================================== */

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((cents || 0) / 100);

const formatDateBR = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

const formatDateTimeBR = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const addMinutesISO = (iso: string, minutes: number): string => {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
};

const startOfDayISO = (iso: string): string => {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

/* =================================================================== */
/* Core: buildWithdrawalBlocks                                           */
/* =================================================================== */

const newBlock = (dateFrom?: string): WithdrawalBlock => ({
  rows: [],
  total_tax: 0,
  fees: 0,
  withdraw_tax: 0,
  antecipation_gap: 0,
  brute: 0,
  brute_without_fees: 0,
  liquid: 0,
  administration_tax: 0,
  saldo: 0,
  sacado: 0,
  sobra: 0,
  date_from: dateFrom,
});

export const buildWithdrawalBlocks = (
  rows: ReportRow[],
): WithdrawalBlock[] => {
  const blocks: WithdrawalBlock[] = [newBlock()];
  let index = 0;

  const listSaques = rows.filter(r => r.operation_type === 'Saque');
  let saque = 0;

  rows.forEach(content => {
    if (content.operation_type === 'Saque') {
      if (content.operation === 'Saque') {
        blocks[index].withdraw_tax += content.operation_fee || 0;
      }
      blocks[index].saldo = content.saldo || 0;
      blocks[index].sacado = content.sacado || 0;

      const operation =
        content.operation === 'Ressarcimento'
          ? 'Estorno de saque'
          : listSaques[saque + 1]?.operation === 'Ressarcimento'
          ? 'Tentativa de Saque com falha'
          : 'Saque';

      blocks[index].rows.push({ ...content, operation_type: operation });
      blocks[index].date_to = content.date;

      if (
        content.operation === 'Saque' &&
        listSaques[saque + 1]?.operation !== 'Ressarcimento'
      ) {
        blocks.push(newBlock(content.date));
        index += 1;
        blocks[index].sobra = blocks[index - 1].saldo;
      }

      saque += 1;
    } else if (content.type === 'ted' && content.special_text) {
      blocks[index].saldo = content.saldo || 0;
      blocks[index].rows.push({
        ...content,
        operation_type: content.special_text,
      });

      let sacado = 0;
      blocks[index].rows.forEach(i => {
        if (i?.operation_type === 'Saque administrativo') {
          sacado += i?.sacado || 0;
        }
      });
      blocks[index].sacado = sacado;
      blocks[index].date_to = content.date;

      blocks.push(newBlock(content.date));
      index += 1;
      blocks[index].sobra = blocks[index - 1].saldo;
      saque += 1;
    } else if (content.operation_type === 'Saque administrativo') {
      blocks[index].total_tax += content.total_tax || 0;
      blocks[index].fees += content.fees || 0;
      blocks[index].brute_without_fees +=
        (content.total || 0) - (content.fees || 0);
      blocks[index].antecipation_gap += content.antecipation_gap || 0;
      blocks[index].administration_tax += content.administration_tax || 0;
      blocks[index].saldo = content.saldo || 0;
      blocks[index].rows.push(content);
    } else {
      blocks[index].brute += content.total || 0;
      blocks[index].total_tax += content.total_tax || 0;
      blocks[index].fees += content.fees || 0;
      blocks[index].brute_without_fees +=
        (content.total || 0) - (content.fees || 0);
      blocks[index].antecipation_gap += content.antecipation_gap || 0;
      blocks[index].administration_tax += content.administration_tax || 0;
      blocks[index].saldo = content.saldo || 0;
      blocks[index].liquid += content.net_total || 0;
      blocks[index].rows.push(content);
    }
  });

  return blocks;
};

export const findLastClosedBlock = (
  blocks: WithdrawalBlock[],
): WithdrawalBlock | null => {
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    if (blocks[i].date_to) return blocks[i];
  }
  return null;
};

/* =================================================================== */
/* Core: analyzeBlock (sem tolerância)                                   */
/* =================================================================== */

export const analyzeBlock = (block: WithdrawalBlock): AnalysisIssue[] => {
  const issues: AnalysisIssue[] = [];

  if (block.sobra !== 0) {
    issues.push({
      code: 'SOBRA',
      title: 'Sobra de saldo da semana anterior não zerada',
      detail: formatCurrency(block.sobra),
    });
  }

  if (block.saldo !== 0) {
    issues.push({
      code: 'SALDO',
      title: 'Saldo final do saque não zerado',
      detail: formatCurrency(block.saldo),
    });
  }

  if (block.brute < block.brute_without_fees) {
    issues.push({
      code: 'BRUTE_LT_BRUTE_WITHOUT_FEES',
      title: 'Bruto menor que Bruto sem Juros',
      detail: `Bruto: ${formatCurrency(block.brute)} · Bruto sem Juros: ${formatCurrency(block.brute_without_fees)}`,
    });
  }

  if (block.brute < block.liquid) {
    issues.push({
      code: 'BRUTE_LT_LIQUID',
      title: 'Bruto menor que Líquido',
      detail: `Bruto: ${formatCurrency(block.brute)} · Líquido: ${formatCurrency(block.liquid)}`,
    });
  }

  const saqueAbs = Math.abs(block.sacado);
  if (block.brute < saqueAbs) {
    issues.push({
      code: 'BRUTE_LT_SACADO',
      title: 'Bruto menor que Valor do Saque',
      detail: `Bruto: ${formatCurrency(block.brute)} · Saque: ${formatCurrency(saqueAbs)}`,
    });
  }

  const expected =
    block.brute -
    block.fees -
    block.total_tax -
    block.antecipation_gap -
    block.administration_tax;
  if (expected !== saqueAbs) {
    issues.push({
      code: 'SAQUE_MISMATCH',
      title:
        'Valor do Saque diferente de (Bruto − Juros − Taxas Venda − Dif. Antecipação − Taxas Administrativas)',
      detail: `Esperado: ${formatCurrency(expected)} · Atual: ${formatCurrency(saqueAbs)} · Diferença: ${formatCurrency(saqueAbs - expected)}`,
    });
  }

  block.rows.forEach(row => {
    const op = row.operation_type;
    const isSaqueRow =
      op === 'Saque' ||
      op === 'Estorno de saque' ||
      op === 'Tentativa de Saque com falha';
    if (!isSaqueRow) {
      const total = (row.total as number) || 0;
      const net = (row.net_total as number) || 0;
      if (total < net) {
        issues.push({
          code: 'ROW_TOTAL_LT_NET',
          title: `Linha "${op || 'Desconhecido'}" com Bruto menor que Líquido`,
          detail: `Bruto: ${formatCurrency(total)} · Líquido: ${formatCurrency(net)}`,
        });
      }
    }
  });

  return issues;
};

/* =================================================================== */
/* Core: simulateStartOfDay                                              */
/* =================================================================== */

export const simulateStartOfDay = (rows: ReportRow[]): ReportRow[] => {
  let lastSaqueIndex = -1;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].operation_type === 'Saque') {
      lastSaqueIndex = i;
      break;
    }
  }
  if (lastSaqueIndex < 0) return rows;

  const modified: ReportRow[] = rows.map((r, i) => {
    if (i !== lastSaqueIndex) return { ...r };
    return {
      ...r,
      date: startOfDayISO(r.date as string),
    };
  });

  modified.sort((a, b) => {
    const ta = new Date(a.date as string).getTime();
    const tb = new Date(b.date as string).getTime();
    return ta - tb;
  });

  let saldo = 0;
  modified.forEach(r => {
    saldo += (r.net_total as number) || 0;
    r.saldo = saldo;
  });

  return modified;
};

/* =================================================================== */
/* Styled                                                                */
/* =================================================================== */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spLg}px;
`;

const SummaryCard = styled.div`
  background: ${canvas};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusLg};
  padding: ${spLg}px;
`;

const SummaryTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${textPrimary};
  margin-bottom: ${spMd}px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${spMd}px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  color: ${textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const MetricValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${textPrimary};
  margin-top: 2px;
`;

const SectionHeading = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${textPrimary};
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spSm}px;
`;

const IssueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spSm}px;
  padding: ${spMd}px;
  background: ${warningLight};
  border-radius: ${radiusMd};
  border: 1px solid ${borderSubtle};
`;

const IssueIconWrap = styled.div`
  color: ${warningText};
  flex-shrink: 0;
  margin-top: 2px;
`;

const IssueText = styled.div`
  flex: 1;
  min-width: 0;
`;

const IssueTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${textPrimary};
`;

const IssueDetail = styled.div`
  font-size: 12px;
  color: ${textSecondary};
  margin-top: 2px;
`;

const SuggestionCard = styled.div<{ $disabled?: boolean }>`
  background: ${surface};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusLg};
  padding: ${spLg}px;
  display: flex;
  flex-direction: column;
  gap: ${spMd}px;
  opacity: ${p => (p.$disabled ? 0.65 : 1)};
`;

const SuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spSm}px;
`;

const SuggestionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${textPrimary};
`;

const SuggestionBody = styled.div`
  font-size: 13px;
  color: ${textSecondary};
  line-height: 1.5;
`;

const SuggestionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spSm}px;
`;

const Banner = styled.div<{ $kind: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${spSm}px;
  padding: ${spMd}px;
  background: ${p => (p.$kind === 'success' ? successLight : warningLight)};
  color: ${p => (p.$kind === 'success' ? successText : warningText)};
  border-radius: ${radiusMd};
  font-size: 13px;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* Orphan-specific */

const OrphanAlertButton = styled.button`
  all: unset;
  display: flex;
  align-items: flex-start;
  gap: ${spMd}px;
  padding: ${spLg}px;
  background: ${warningLight};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusLg};
  color: ${textPrimary};
  cursor: pointer;
  transition: filter 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(0.98);
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;

const OrphanAlertIcon = styled.div`
  color: ${warningText};
  flex-shrink: 0;
  margin-top: 2px;
`;

const OrphanAlertBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrphanAlertTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${textPrimary};
  margin-bottom: 4px;
`;

const OrphanAlertDetail = styled.div`
  font-size: 13px;
  color: ${textSecondary};
  line-height: 1.5;
`;

const CandidateCard = styled.div`
  background: ${surface};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusLg};
  padding: ${spLg}px;
  display: flex;
  flex-direction: column;
  gap: ${spMd}px;
`;

const CandidateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spMd}px;
`;

const ProjectDiff = styled.div`
  background: ${canvas};
  border: 1px solid ${borderSubtle};
  border-radius: ${radiusMd};
  padding: ${spMd}px;
  display: flex;
  flex-direction: column;
  gap: ${spSm}px;
  font-size: 13px;
`;

const ProjectDiffRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spMd}px;
`;

const ProjectDiffLabel = styled.span`
  color: ${textTertiary};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const ProjectDiffValue = styled.span<{ $kind?: 'from' | 'to' }>`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: ${p =>
    p.$kind === 'to'
      ? successText
      : p.$kind === 'from'
      ? dangerText
      : textPrimary};
  word-break: break-all;
  text-align: right;
`;

/* =================================================================== */
/* Orphan Saque Analysis                                                 */
/* =================================================================== */

interface OrphanAnalysisProps {
  item: AnalysisItemRef;
}

const OrphanSaqueAnalysis: React.FC<OrphanAnalysisProps> = ({ item }) => {
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [candidates, setCandidates] = useState<OrphanCandidate[]>([]);
  const [updatingId, setUpdatingId] = useState<string | null>(null);


  console.log(item)

  const [banner, setBanner] =
    useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // item.saque vem em REAIS (backend faz /100). Convertemos para centavos
  // para comparar com o campo amount de finance-report.
  const saqueAmountCents =
    typeof item.saque === 'number' ? Math.round(item.saque * 100) : null;

  const handleSearch = async () => {
    if (!item.id) {
      setBanner({ kind: 'error', text: 'ID do saque (Pagar.me) indisponível.' });
      return;
    }
    if (saqueAmountCents === null) {
      setBanner({ kind: 'error', text: 'Valor do saque indisponível.' });
      return;
    }

    setSearching(true);
    setBanner(null);
    try {
      // ASSUMPTION: GET /finance-report?amount=<centavos>
      // Ajustar se seu endpoint usar outra rota ou outra unidade.

     


      const response = await api.get('/finance-report', {
        params: { limitItems: 150, where: {  "type" : "ted" }},
      });


      const saque = parseInt((((item?.saque || 0) * 100) * -1)?.toString(),10);


      const all = response?.data?.rows?.filter( i => i?.data.amount ===  saque)
 
console.log(all, saque, item)
      // Filtra client-side por data.id === saque.id
      const matching = all.filter(c => c.data?.movement_object?.id?.toString() === item?.id?.toString());
      setCandidates(matching);
      setSearched(true);
    } catch (err) {
      console.error(err);
      setBanner({
        kind: 'error',
        text: 'Erro ao buscar em finance-report.',
      });
    } finally {
      setSearching(false);
    }
  };

  const handleUpdateProject = async (candidate: OrphanCandidate) => {
    setUpdatingId(candidate._id);
    setBanner(null);
    try {
      // ASSUMPTION: POST /finance-report-reproject/:id { project_id }
      // Segue o mesmo padrão do /finance-report-redate.
      await api.put(`/finance-report/${candidate._id}/project_id`, {
        project_id: item.url,
      });

      setBanner({
        kind: 'success',
        text: 'project_id atualizado com sucesso.',
      });
      // Remove o candidato já corrigido da lista
      setCandidates(prev => prev.filter(c => c._id !== candidate._id));
    } catch (err) {
      console.error(err);
      setBanner({
        kind: 'error',
        text: 'Erro ao atualizar o project_id.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <>
      {banner && (
        <Banner $kind={banner.kind}>
          {banner.kind === 'success' ? (
            <FaCircle size={16} />
          ) : (
            <FiAlertTriangle size={16} />
          )}
          {banner.text}
        </Banner>
      )}

      <OrphanAlertButton
        type="button"
        onClick={handleSearch}
        disabled={searching}
      >
        <OrphanAlertIcon>
          <FiAlertTriangle size={20} />
        </OrphanAlertIcon>
        <OrphanAlertBody>
          <OrphanAlertTitle>
            Saque não encontrado no relatório
          </OrphanAlertTitle>
          <OrphanAlertDetail>
            O saque existe na Pagar.me mas não apareceu nas linhas deste
            projeto. Ele pode ter sido registrado em outro project_id.
            <br />
            <strong>ID Pagar.me:</strong> {item.id || '—'} ·{' '}
            <strong>Valor:</strong>{' '}
            {saqueAmountCents !== null
              ? formatCurrency(saqueAmountCents)
              : '—'}
            <br />
            {searching
              ? 'Buscando candidatos em finance-report…'
              : searched
              ? 'Clique para buscar novamente.'
              : 'Clique para buscar em finance-report.'}
          </OrphanAlertDetail>
        </OrphanAlertBody>
      </OrphanAlertButton>

      {searched && candidates.length === 0 && (
        <Banner $kind="error">
          <FaSearch size={16} />
          Nenhum registro em finance-report bateu com o mesmo amount e
          data.id do saque.
        </Banner>
      )}

      {candidates.length > 0 && (
        <>
          <SectionHeading>
            {candidates.length === 1
              ? 'Candidato encontrado'
              : `${candidates.length} candidatos encontrados`}
          </SectionHeading>
          {candidates.map(c => (
            <CandidateCard key={c._id}>
              <CandidateGrid>
                <div>
                  <MetricLabel>Valor</MetricLabel>
                  <MetricValue>
                    {formatCurrency(c.amount || 0)}
                  </MetricValue>
                </div>
                <div>
                  <MetricLabel>Criado em</MetricLabel>
                  <MetricValue>{formatDateTimeBR(c.createdAt)}</MetricValue>
                </div>
                <div>
                  <MetricLabel>data.id</MetricLabel>
                  <MetricValue
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12,
                    }}
                  >
                    {c.data?.id || '—'}
                  </MetricValue>
                </div>
              </CandidateGrid>

              <ProjectDiff>
                <ProjectDiffRow>
                  <ProjectDiffLabel>project_id atual</ProjectDiffLabel>
                  <ProjectDiffValue $kind="from">
                    {c.project_id}
                  </ProjectDiffValue>
                </ProjectDiffRow>
                <ProjectDiffRow>
                  <ProjectDiffLabel>project_id após</ProjectDiffLabel>
                  <ProjectDiffValue $kind="to">{item.url}</ProjectDiffValue>
                </ProjectDiffRow>
              </ProjectDiff>

              <SuggestionFooter>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={updatingId === c._id}
                  disabled={
                    updatingId === c._id || c.project_id === item.url
                  }
                  onClick={() => handleUpdateProject(c)}
                >
                  {c.project_id === item.url
                    ? 'Já está correto'
                    : 'Atualizar projeto'}
                </Button>
              </SuggestionFooter>
            </CandidateCard>
          ))}
        </>
      )}
    </>
  );
};

/* =================================================================== */
/* Componente principal                                                  */
/* =================================================================== */

interface WithdrawAnalysisContentProps {
  item: AnalysisItemRef;
  isInvoice: boolean;
  rows: ReportRow[];
  onRefresh: () => Promise<ReportRow[] | null>;
  modalKey: string;
}

const WithdrawAnalysisContent: React.FC<WithdrawAnalysisContentProps> = ({
  item,
  rows: initialRows,
  onRefresh,
  modalKey,
}) => {
  const [rows, setRows] = useState<ReportRow[]>(initialRows);
  const [applying1, setApplying1] = useState(false);
  const [applying2, setApplying2] = useState(false);
  const [banner, setBanner] =
    useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const { removeModal } = useModal();

  const { lastBlock, issues } = useMemo(() => {
    const blocks = buildWithdrawalBlocks(rows);
    const last = findLastClosedBlock(blocks);
    return {
      lastBlock: last,
      issues: last ? analyzeBlock(last) : [],
    };
  }, [rows]);

  // Órfão: a Pagar.me tem saque (item.id) mas nenhuma row do relatório
  // tem movement_id === item.id. As rows vêm de check-saldo-static, onde
  // movement_id corresponde ao id do saque na Pagar.me.
  const isOrphan = useMemo(() => {
    if (!item.id) return false;
    return !rows.some(r => r.movement_id === item.id);
  }, [rows, item.id]);

  const simulation = useMemo(() => {
    if (!lastBlock || lastBlock.saldo === 0) return null;
    const simRows = simulateStartOfDay(rows);
    const simBlocks = buildWithdrawalBlocks(simRows);
    const simLast = findLastClosedBlock(simBlocks);
    return {
      simulatedSaldo: simLast?.saldo ?? 0,
      zeroes: simLast?.saldo === 0,
    };
  }, [rows, lastBlock]);

  // Nenhum bloco fechado encontrado nas linhas
  if (!lastBlock) {
    // Mas a Pagar.me diz que existe um saque que não está no relatório → órfão
    if (isOrphan) {
      return (
        <Wrapper>
          <OrphanSaqueAnalysis item={item} />
          <FooterRow>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => removeModal(modalKey)}
            >
              Fechar
            </Button>
          </FooterRow>
        </Wrapper>
      );
    }
    // Sem saque de verdade
    return (
      <EmptyState
        title="Sem saques para analisar"
        description="Este projeto não tem um saque identificado no relatório."
      />
    );
  }

  const lastSaqueRow = lastBlock.rows.find(
    r => r.operation_type === 'Saque',
  );
  const hasSaqueId = !!lastSaqueRow?._id;

  const handleApplyStartOfDay = async () => {
    if (!lastSaqueRow?._id || !lastSaqueRow?.date) {
      setBanner({ kind: 'error', text: 'Saque sem _id ou data.' });
      return;
    }
    const newDate = startOfDayISO(lastSaqueRow.date as string);

    setApplying1(true);
    setBanner(null);
    try {
      await api.post(`/finance-report-redate/${lastSaqueRow._id}`, {
        date: newDate,
      });
      setBanner({
        kind: 'success',
        text: 'Data do saque atualizada. Recarregando…',
      });
      const fresh = await onRefresh();
      if (fresh) setRows(fresh);
    } catch (err) {
      console.error(err);
      setBanner({ kind: 'error', text: 'Erro ao aplicar a sugestão.' });
    } finally {
      setApplying1(false);
    }
  };

  const handleApplyBlockUnblock = async () => {
    if (!item.payment_id) {
      setBanner({ kind: 'error', text: 'payment_id não disponível.' });
      return;
    }
    if (!lastSaqueRow?.date) {
      setBanner({ kind: 'error', text: 'Saque sem data.' });
      return;
    }

    const saldoAbs = Math.abs(lastBlock.saldo);
    const blockValue = -saldoAbs;
    const unblockValue = saldoAbs;
    const blockDate = addMinutesISO(lastSaqueRow.date as string, -1);
    const unblockDate = addMinutesISO(lastSaqueRow.date as string, 1);

    setApplying2(true);
    setBanner(null);
    try {
      await api.post('/finance-report', {
        payment_id: item.payment_id,
        project_id: item.url,
        type: null,
        special_text: 'Saldo bloqueado para análise',
        special_value: String(blockValue),
        date: blockDate,
      });
      await api.post('/finance-report', {
        payment_id: item.payment_id,
        project_id: item.url,
        type: null,
        special_text: 'Saldo desbloqueado para saque',
        special_value: String(unblockValue),
        date: unblockDate,
      });
      setBanner({
        kind: 'success',
        text: 'Bloqueio e desbloqueio criados. Recarregando…',
      });
      const fresh = await onRefresh();
      if (fresh) setRows(fresh);
    } catch (err) {
      console.error(err);
      setBanner({ kind: 'error', text: 'Erro ao criar os registros.' });
    } finally {
      setApplying2(false);
    }
  };

  const hasSaldo = lastBlock.saldo !== 0;

  return (
    <Wrapper>
      {banner && (
        <Banner $kind={banner.kind}>
          {banner.kind === 'success' ? (
            <FaCircle size={16} />
          ) : (
            <FiAlertTriangle size={16} />
          )}
          {banner.text}
        </Banner>
      )}

      {isOrphan && <OrphanSaqueAnalysis item={item} />}

      <SummaryCard>
        <SummaryTitle>
          Último saque — {formatDateBR(lastBlock.date_to)}
        </SummaryTitle>
        <SummaryGrid>
          <div>
            <MetricLabel>Bruto</MetricLabel>
            <MetricValue>{formatCurrency(lastBlock.brute)}</MetricValue>
          </div>
          <div>
            <MetricLabel>Líquido</MetricLabel>
            <MetricValue>{formatCurrency(lastBlock.liquid)}</MetricValue>
          </div>
          <div>
            <MetricLabel>Valor do Saque</MetricLabel>
            <MetricValue>
              {formatCurrency(Math.abs(lastBlock.sacado))}
            </MetricValue>
          </div>
          <div>
            <MetricLabel>Saldo</MetricLabel>
            <MetricValue
              style={{
                color:
                  lastBlock.saldo > 0
                    ? successText
                    : lastBlock.saldo < 0
                    ? dangerText
                    : textSecondary,
              }}
            >
              {formatCurrency(lastBlock.saldo)}
            </MetricValue>
          </div>
          <div>
            <MetricLabel>Sobra anterior</MetricLabel>
            <MetricValue
              style={{
                color:
                  lastBlock.sobra > 0
                    ? successText
                    : lastBlock.sobra < 0
                    ? dangerText
                    : textSecondary,
              }}
            >
              {formatCurrency(lastBlock.sobra)}
            </MetricValue>
          </div>
          <div>
            <MetricLabel>Taxa de Saque</MetricLabel>
            <MetricValue>{formatCurrency(lastBlock.withdraw_tax)}</MetricValue>
          </div>
        </SummaryGrid>
      </SummaryCard>

      <SectionHeading>Inconsistências</SectionHeading>
      {issues.length === 0 ? (
        <Banner $kind="success">
          <FaCircle size={16} />
          Nenhuma inconsistência detectada no último saque.
        </Banner>
      ) : (
        <IssuesList>
          {issues.map((issue, i) => (
            <IssueItem key={`${issue.code}-${i}`}>
              <IssueIconWrap>
                <FiAlertTriangle size={16} />
              </IssueIconWrap>
              <IssueText>
                <IssueTitle>{issue.title}</IssueTitle>
                {issue.detail && <IssueDetail>{issue.detail}</IssueDetail>}
              </IssueText>
            </IssueItem>
          ))}
        </IssuesList>
      )}

      {hasSaldo && (
        <>
          <SectionHeading>Sugestões</SectionHeading>

          {simulation?.zeroes ? (
            <SuggestionCard>
              <SuggestionHeader>
                <FaClock size={18} />
                <SuggestionTitle>
                  1. Mover saque para o início do dia
                </SuggestionTitle>
              </SuggestionHeader>
              <SuggestionBody>
                Reprocessando o saque no início do dia em que ele ocorreu,
                o saldo passaria de{' '}
                <strong>{formatCurrency(lastBlock.saldo)}</strong> para{' '}
                <strong>{formatCurrency(simulation.simulatedSaldo)}</strong>.
                Isso zera a inconsistência.
              </SuggestionBody>
              <SuggestionFooter>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={applying1}
                  disabled={applying1 || !hasSaqueId}
                  onClick={handleApplyStartOfDay}
                >
                  Aplicar
                </Button>
              </SuggestionFooter>
            </SuggestionCard>
          ) : (
            <SuggestionCard $disabled>
              <SuggestionHeader>
                <FaClock size={18} />
                <SuggestionTitle>
                  1. Mover saque para o início do dia
                </SuggestionTitle>
              </SuggestionHeader>
              <SuggestionBody>
                {simulation ? (
                  <>
                    Mesmo movendo o saque para o início do dia o saldo não
                    zeraria (ficaria em{' '}
                    <strong>
                      {formatCurrency(simulation.simulatedSaldo)}
                    </strong>
                    ). Use a opção 2.
                  </>
                ) : (
                  'Não foi possível simular esta opção.'
                )}
              </SuggestionBody>
            </SuggestionCard>
          )}

          <SuggestionCard $disabled={!item.payment_id}>
            <SuggestionHeader>
              <FaClock size={18} />
              <SuggestionTitle>
                2. Bloquear e desbloquear saldo ao redor do saque
              </SuggestionTitle>
            </SuggestionHeader>
            <SuggestionBody>
              Cria dois registros para zerar o saldo da semana:
              <br />
              • <strong>Bloqueio</strong> em{' '}
              {formatDateTimeBR(
                addMinutesISO(lastSaqueRow?.date as string, -1),
              )}{' '}
              de <strong>{formatCurrency(-Math.abs(lastBlock.saldo))}</strong>
              <br />
              • <strong>Desbloqueio</strong> em{' '}
              {formatDateTimeBR(
                addMinutesISO(lastSaqueRow?.date as string, 1),
              )}{' '}
              de <strong>{formatCurrency(Math.abs(lastBlock.saldo))}</strong>
              {!item.payment_id && (
                <>
                  <br />
                  <em>
                    Aguardando o payment_id no retorno do
                    checkFullReportJSON.
                  </em>
                </>
              )}
            </SuggestionBody>
            <SuggestionFooter>
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={applying2}
                disabled={applying2 || !item.payment_id}
                onClick={handleApplyBlockUnblock}
              >
                Aplicar
              </Button>
            </SuggestionFooter>
          </SuggestionCard>
        </>
      )}

      <FooterRow>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => removeModal(modalKey)}
        >
          Fechar
        </Button>
      </FooterRow>
    </Wrapper>
  );
};

/* =================================================================== */
/* Hook abridor                                                          */
/* =================================================================== */

interface OpenAnalysisArgs {
  item: AnalysisItemRef;
  isInvoice: boolean;
  rows: ReportRow[];
  onRefresh: () => Promise<ReportRow[] | null>;
}

export const useOpenWithdrawAnalysisModal = () => {
  const { addModalV2 } = useModal();

  return (args: OpenAnalysisArgs) => {
    const key = `withdraw-analysis-${args.item.url}`;
    addModalV2({
      key,
      title: `Análise — ${args.item.code || args.item.url}`,
    
      content: (
        <WithdrawAnalysisContent
          modalKey={key}
          item={args.item}
          isInvoice={args.isInvoice}
          rows={args.rows}
          onRefresh={args.onRefresh}
        />
      ),
    });
  };
};

export default WithdrawAnalysisContent;
